/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited  2023.
 */

import { FC, useContext } from 'react';
import { Paper, Stack, Typography, Grid } from '@mui/material';
import { StoreAttribute } from '@/data/types/Store';
import { storeLocatorInfoBoxSX } from '@/components/content/StoreLocator/styles/infoBox';
import { useLocalization } from '@/data/Localization';
import HTMLReactParser from 'html-react-parser';
import { ContentContext } from '@/data/context/content';
import { useStoreLocator } from '@/data/Content/StoreLocator';
import { infoBoxStackLevelThreeSX } from '@/components/content/StoreLocator/styles/infoBoxStackLevelThree';
import { infoBoxStackLevelTwoSX } from '@/components/content/StoreLocator/styles/infoBoxStackLevelTwo';
import { infoBoxStackLevelOneSX } from '@/components/content/StoreLocator/styles/infoBoxStackLevelOne';
import { nameStyle } from '@/components/content/StoreLocator/styles/SideList/titleTypography';
import { stylehowToGet } from '@/components/content/StoreLocator/styles/SideList/titleTypography';
import DirectionsIcon from '@mui/icons-material/Directions';
import PhoneIcon from '@mui/icons-material/Phone';
import { combineSX } from '@/utils/combineSX';
import ClearIcon from '@mui/icons-material/Clear';
import { motion } from 'framer-motion';

export const StoreLocatorInfoBox: FC = () => {
	const { hidden, locator, clickedIndex, showDirection, expand, noDirectionPath, setHidden } =
		useContext(ContentContext) as ReturnType<typeof useStoreLocator>;

	const localization = useLocalization('StoreLocator');

	const store = locator.storeList[clickedIndex];
	const storeName = store?.storeName;
	const address = store?.storeFullAddress;
	const storeHours = store?.attributes?.find(
		(attribute: StoreAttribute) => attribute.name === 'StoreHours'
	);

	const getGoogleMapsLink = () => {
		if (!store || !store.coordinates) return '#';
		const { lat, lng } = store.coordinates;
		return `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;
	};

	const handleClear = () => {
		setHidden(true);
	};

	return (
		<motion.div
			initial={{ opacity: 0 }}
			animate={{ opacity: hidden ? 0 : 1 }}
			transition={{ duration: 0.3 }}
		>
			<Paper sx={combineSX([storeLocatorInfoBoxSX, { boxShadow: 'none' }])}>
				<Stack direction="row" spacing={2} sx={infoBoxStackLevelOneSX}>
					<Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={infoBoxStackLevelTwoSX}>
						<Stack sx={infoBoxStackLevelThreeSX} spacing={0}>
							{storeName ? (
								<Typography variant="h4" sx={nameStyle}>
									{storeName}
								</Typography>
							) : null}
							{address ? (
								<Typography sx={{ marginTop: '3px', fontSize: 'var(--font-size-body)' }}>
									{store.attributes[0].value
										.replace(/\./g, '')
										.replace(/(\d{2}:\d{2})\sa\s(\d{2}:\d{2})/g, '$1 - $2')
										.replace(/(\w+)\sa\s(\w+)/g, '$1 - $2:')
										.replace(/(\b\w+\b)\s(\d{2}:\d{2}\s-\s\d{2}:\d{2}\shrs)/g, '$1: $2')
										.replace(/,\sDom:/, ' y Dom:')}
								</Typography>
							) : null}
							{address ? (
								<Typography sx={{ marginTop: '3px', fontSize: 'var(--font-size-body)' }}>
									{address}
								</Typography>
							) : null}
							{expand ? (
								<Grid sx={{ display: 'flex', alignItems: 'center' }}>
									<Typography sx={combineSX([stylehowToGet, { marginTop: '2px' }])}>
										<a
											href={`tel:+52${store?.phone}`}
											style={{ textDecoration: 'none', color: 'inherit' }}
										>
											<PhoneIcon sx={{ marginBottom: '-6px' }} />
											{store?.phone.replace(/(\d{3})(\d{3})(\d{4})/, '$1 $2 $3')}
										</a>
									</Typography>
									<Typography
										data-testid="store-locator-info-box-get-directions"
										id="store-locator-info-box-get-directions"
										onClick={() => window.open(getGoogleMapsLink(), '_blank')}
										sx={combineSX([stylehowToGet, { marginLeft: '25px' }])}
									>
										<DirectionsIcon />
										{localization.getDirections.t()}
									</Typography>
									{noDirectionPath && showDirection ? (
										<Typography variant="caption" color="textSecondary">
											{localization.directionNotAvailable.t()}
										</Typography>
									) : null}
									{storeHours ? (
										<>
											<Typography>{storeHours.displayName + ':'}</Typography>
											<Typography>{HTMLReactParser(storeHours.displayValue)}</Typography>
										</>
									) : null}
								</Grid>
							) : null}
						</Stack>
					</Stack>
					<ClearIcon
						sx={{
							fontSize: '22px',
							position: 'absolute',
							top: '15px',
							right: '20px',
							cursor: 'pointer',
						}}
						onClick={handleClear}
					/>
				</Stack>
			</Paper>
		</motion.div>
	);
};
